import { Helmet } from "react-helmet-async";

export const SITE_URL = "https://www.deepaarogya.com";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/case-sheet-preview.png`;

const ENTITY_DESCRIPTION =
  "Deepaarogya AI is an AI hospital and clinic operating system (EMR software) for India — OPD, IPD, pharmacy, billing, physiotherapy, nutrition, and clinical AI agents on one ABDM-ready patient record.";

export default function SeoHead({
  title,
  description,
  path = "/",
  jsonLd = null,
  ogImage = DEFAULT_OG_IMAGE,
  breadcrumbs = null,
}) {
  const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
  const fullTitle = title.includes("Deepaarogya") ? title : `${title} | Deepaarogya AI`;

  const graphItems = [];
  if (breadcrumbs?.length) graphItems.push(breadcrumbJsonLd(breadcrumbs));
  if (jsonLd) {
    if (jsonLd["@graph"]) graphItems.push(...jsonLd["@graph"]);
    else graphItems.push(jsonLd);
  }
  const mergedLd =
    graphItems.length === 0
      ? null
      : graphItems.length === 1
        ? graphItems[0]
        : { "@context": "https://schema.org", "@graph": graphItems };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Deepaarogya AI" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {mergedLd && (
        <script type="application/ld+json">{JSON.stringify(mergedLd)}</script>
      )}
    </Helmet>
  );
}

export function buildJsonLdGraph(...nodes) {
  return {
    "@context": "https://schema.org",
    "@graph": nodes,
  };
}

export function buildFaqJsonLd(faqs) {
  return {
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };
}

export function breadcrumbJsonLd(items) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
    })),
  };
}

export function organizationJsonLd() {
  return {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "Deepaarogya AI",
    alternateName: ["Deepaarogya", "Deepaarogya AI Hospital OS"],
    url: SITE_URL,
    logo: `${SITE_URL}/brand/deepaarogya-logo.png`,
    description: ENTITY_DESCRIPTION,
    areaServed: {
      "@type": "Country",
      name: "India",
    },
    knowsAbout: [
      "Electronic Medical Records",
      "Hospital Information System",
      "ABDM",
      "ABHA",
      "Clinical AI",
      "Physiotherapy clinic software",
      "Orthopedic EMR",
      "Pharmacy management",
      "Google Drive patient sync",
      "WhatsApp e-prescription and reminders",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "contact@deepaarogya.com",
      telephone: "+91-8979523908",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
    sameAs: [
      "https://blog.deepaarogya.com/",
      "https://www.linkedin.com/company/deepaarogya",
      "https://www.facebook.com/people/Deepaarogya-AI/61575079247029/",
    ],
  };
}

const AGENT_FEATURES = [
  "EMR and case sheet",
  "OPD token queue",
  "IPD case sheets",
  "Pharmacy and dispensing",
  "GST billing",
  "Physiotherapy protocols",
  "Radiology queue",
  "Nutrition AI",
  "Discharge Summary Agent",
  "Visit Summary Agent",
  "SOAP Agent (22 languages)",
  "Doc Agent",
  "Deep Healthcare Agent",
  "WhatsApp e-prescription",
  "WhatsApp follow-up and reminders",
  "Google Drive patient file sync",
  "Google Meet telemedicine",
  "ABDM-ready ABHA linking",
];

export function softwareJsonLd() {
  return {
    "@type": "SoftwareApplication",
    "@id": `${SITE_URL}/#software`,
    name: "Deepaarogya AI",
    url: SITE_URL,
    applicationCategory: "HealthApplication",
    applicationSubCategory: "Electronic Medical Record",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: "500",
      priceCurrency: "INR",
      url: `${SITE_URL}/pricing`,
      description: "Clinic Lite plan from ₹500/month + GST",
    },
    publisher: {
      "@id": `${SITE_URL}/#organization`,
    },
    description: ENTITY_DESCRIPTION,
    featureList: AGENT_FEATURES,
  };
}

export function websiteJsonLd() {
  return {
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: "Deepaarogya AI",
    url: SITE_URL,
    description: ENTITY_DESCRIPTION,
    publisher: {
      "@id": `${SITE_URL}/#organization`,
    },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://blog.deepaarogya.com/?s={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function howToJsonLd(steps) {
  return {
    "@type": "HowTo",
    name: "How to go live with Deepaarogya AI at your clinic",
    description:
      "Digitize your clinic workflow, onboard your team, and enable clinical AI agents on one ABDM-ready patient record.",
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.title,
      text: s.desc,
    })),
  };
}

export function collectionPageJsonLd({ name, description, path, items }) {
  const pageUrl = `${SITE_URL}${path === "/" ? "" : path}`;
  return {
    "@type": "CollectionPage",
    "@id": `${pageUrl}#collection`,
    name,
    description,
    url: pageUrl,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: items.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.name,
        url: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
      })),
    },
  };
}

export function webPageJsonLd({ name, description, path, dateModified }) {
  const pageUrl = `${SITE_URL}${path === "/" ? "" : path}`;
  return {
    "@type": "WebPage",
    "@id": `${pageUrl}#webpage`,
    name,
    description,
    url: pageUrl,
    ...(dateModified ? { dateModified } : {}),
    inLanguage: "en-IN",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

export function softwareApplicationPageJsonLd({ name, description, path, features = [] }) {
  const pageUrl = `${SITE_URL}${path === "/" ? "" : path}`;
  return {
    "@type": "SoftwareApplication",
    "@id": `${pageUrl}#software`,
    name,
    description,
    url: pageUrl,
    applicationCategory: "HealthApplication",
    applicationSubCategory: "Physiotherapy clinic software",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: "500",
      priceCurrency: "INR",
      url: `${SITE_URL}/pricing`,
      description: "Clinic Lite plan from ₹500/month + GST",
    },
    publisher: { "@id": `${SITE_URL}/#organization` },
    ...(features.length ? { featureList: features } : {}),
  };
}
